import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, Typography, Button, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import ListingTableTemplate from '@/components/ListingTableTemplate';
import { Close as CloseIcon } from '@mui/icons-material';
import { Contest } from '../../api/contest';

interface IAsset {
    id: string;
    wp_user_id: string;
    username: string;
    created_at: string;
    folder_name: string;
}

interface IMediaAssets {
  imageGenerateData: {
    id: string;
    url_image_generate: string;
    username: string;
    created_at: string;
    publish_date: string | null;
  };
  videoGenerateData: {
    id: string;
    url_video_generate: string;
    username: string;
    created_at: string;
    publish_date: string | null;
  };
}

const MyAsset = () => {
    const [rows, setRows] = useState<IAsset[]>([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<IMediaAssets | null>(null);

    const fetchAssets = async () => {
        setLoading(true);
        try {
            const response = await Contest.getDataAsset();
            setRows(response.data);
        } catch (error) {
            console.error('Error fetching assets:', error);
        } finally {
            setLoading(false);
        }
    };

    const reloadData = useCallback(() => {
        fetchAssets();
    }, []);

    useEffect(() => {
        reloadData();
    }, []);

    const handleFolderClick = async (id: string) => {
        try {
            // const response = await fetch(`https://qa.pythaverse.space/wp-content/plugins/student_workspace_v3/api/my-contest/getMediaAssets.php?id=${id}`);
            // const data = await response.json();
            const data = await Contest.getMediaAsset(id);
            console.log("data media", data);
            if (data.status === 'success') {
                setSelectedMedia(data);
                setOpenDialog(true);
            }
        } catch (error) {
            console.error('Error fetching media assets:', error);
        }
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const columns: GridColDef[] = [
        { 
            field: 'id', 
            headerName: '#', 
            width: 70,
            renderCell: (params) => rows.findIndex(row => row.id === params.row.id) + 1
        },
        { 
            field: 'folder_name', 
            headerName: 'Folder Name', 
            flex: 1,
            renderCell: (params) => (
                <Box
                    sx={{ 
                        cursor: 'pointer', 
                        '&:hover': { 
                            textDecoration: 'underline',
                            color: '#1976d2'
                        }, 
                        color: '#0d47a1'
                    }}
                    onClick={() => handleFolderClick(params.row.id)}
                >
                    {params.value}
                </Box>
            )
        },
        { 
            field: 'created_at', 
            headerName: 'Created Date', 
            flex: 1,
            valueFormatter: (params) => {
                return params.value || '-';
            }
        }
    ];

    return (
        <>
            <Box sx={{ p: 3, bgcolor: "white", borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{
                        // mb: 2,
                        color: '#0070C0',
                        // fontSize: '20px',
                        fontWeight: 600
                    }}>
                        My Assets
                    </Typography>
                    <Button 
                        variant="contained" 
                        href={process.env.AVATAR_PUBLIC_URL}
                        target="_blank"
                    >
                        View Public Avatars
                    </Button>
                </Box>
                <ListingTableTemplate
                    pageTitle=""
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    onMenuClick={reloadData}
                    buttonOptions={[{ label: 'Reload data' }]}
                    sortModel={[{ field: 'created_at', sort: 'desc' }]}
                    getRowId={(row: IAsset) => row.id}
                    disableRowSelectionOnClick={true}
                />
            </Box>

            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <IconButton
                    aria-label="close"
                    onClick={() => setOpenDialog(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    {selectedMedia && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                {/* Video Section */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6">Details</Typography>
                                    {/*<Box sx={{ mt: 1, mb: 2 }}>*/}
                                    {/*    <Button variant="outlined" size="small">Private</Button>*/}
                                    {/*</Box>*/}
                                    <video 
                                        controls 
                                        style={{ width: '100%', maxHeight: '400px' }}
                                        src={`${process.env.URL_VIDEO_GENERATE}${selectedMedia.videoGenerateData.url_video_generate}`}
                                    />
                                    <Box sx={{ mt: 2 }}>
                                        <Typography><strong>Username:</strong> {selectedMedia.videoGenerateData.username}</Typography>
                                        <Typography><strong>Created Date:</strong> {selectedMedia.videoGenerateData.created_at}</Typography>
                                        <Typography><strong>Public Date:</strong> {selectedMedia.videoGenerateData.publish_date || 'Not Published'}</Typography>
                                    </Box>
                                    <Button 
                                        variant="contained" 
                                        fullWidth 
                                        sx={{ mt: 2 }}
                                        onClick={() => handleDownload(
                                            `${process.env.URL_VIDEO_GENERATE}${selectedMedia.videoGenerateData.url_video_generate}`,
                                            `video_${selectedMedia.videoGenerateData.id}.mp4`
                                        )}
                                    >
                                        Download Video
                                    </Button>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Facebook
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            TikTok
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                                            disabled={true}
                                        >
                                            WhatsApp
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Twitter
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Zalo
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Image Section */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6">Details</Typography>
                                    {/*<Box sx={{ mt: 1, mb: 2 }}>*/}
                                    {/*    <Button variant="outlined" size="small">Private</Button>*/}
                                    {/*</Box>*/}
                                    <img 
                                        src={`${process.env.URL_VIDEO_GENERATE}${selectedMedia.imageGenerateData.url_image_generate}`}
                                        alt="Generated"
                                        style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                    />
                                    <Box sx={{ mt: 2 }}>
                                        <Typography><strong>Username:</strong> {selectedMedia.imageGenerateData.username}</Typography>
                                        <Typography><strong>Created Date:</strong> {selectedMedia.imageGenerateData.created_at}</Typography>
                                        <Typography><strong>Public Date:</strong> {selectedMedia.imageGenerateData.publish_date || 'Not Published'}</Typography>
                                    </Box>
                                    <Button 
                                        variant="contained" 
                                        fullWidth 
                                        sx={{ mt: 2 }}
                                        onClick={() => handleDownload(
                                            `${process.env.URL_VIDEO_GENERATE}${selectedMedia.imageGenerateData.url_image_generate}`,
                                            `image_${selectedMedia.imageGenerateData.id}.png`
                                        )}
                                    >
                                        Download Image
                                    </Button>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Facebook
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            TikTok
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                                            disabled={true}
                                        >
                                            WhatsApp
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Twitter
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#0d95e8' } }}
                                            disabled={true}
                                        >
                                            Zalo
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MyAsset; 